import {on} from "../util";

declare const eo_version : string;

export default class {
    static init = () => {
        paperdoll();
    }
}

// ---------------------------------------------------------------------------
// D-04/D-07 — Paperdoll derived character-stats panel. Payload shape mirrors
// PaperdollModel::getClasses()/getEquipmentStats() (209-04 Task 1) + the
// formula constants emitted inline in paperdoll.latte's data-eo-ppd-stats-json
// attribute (209-04 Task 2).
// ---------------------------------------------------------------------------

interface EoPpdItemStats {
    power: number | null;
    accuracy: number | null;
    defense: number | null;
    dexterity: number | null;
    vitality: number | null;
    aura: number | null;
    hit_points: number | null;
    tech_points: number | null;
    damage_min: number | null;
    damage_max: number | null;
    hit_rate: number | null;
    armor: number | null;
    evade: number | null;
    weight: number | null;
    critical_hit: number | null;
    range: number | null;
}

interface EoPpdClass {
    id: number;
    name: string;
    type: number | null;
    pow: number | null;
    acc: number | null;
    def: number | null;
    dex: number | null;
    vit: number | null;
    aur: number | null;
}

interface EoPpdEquipmentStats {
    hats: Record<string, EoPpdItemStats>;
    clothing: Record<string, EoPpdItemStats>;
    boots: Record<string, EoPpdItemStats>;
    weapons: Record<string, EoPpdItemStats>;
    shields: Record<string, EoPpdItemStats>;
}

interface EoPpdStatsPayload {
    classes: EoPpdClass[];
    equipmentStats: EoPpdEquipmentStats;
    formula: {
        base: {
            baseConst: number;
            levelMult: number;
            tierBlockMult: number;
            hpConMult: number;
            tpIntMult: number;
            tpWisMult: number;
            spBaseConst: number;
            spTierBlockMult: number;
            spConDivisor: number;
            weightBaseConst: number;
        };
        bonusMultipliers: {
            vitalityToHp: number;
            auraToTp: number;
            powerToDamage: number;
            accuracyToHitRate: number;
            dexterityToHitRate: number;
            defenseToArmor: number;
            dexterityToEvade: number;
            accuracyToEvade: number;
        };
    };
}

/**
 * computeCharacterStats — wires the derived character-stats panel's live
 * recompute-on-change (D-07). Mirrors paperdoll()'s own container-presence
 * idempotency guard (`if(!container) return;`) so this only wires once per
 * page load. Reads the equipmentStats + classes + formula payload ONCE
 * (data-eo-ppd-stats-json), then recomputes purely client-side on every
 * attribute/level/class/equipment `change` — no network round-trip.
 */
function computeCharacterStats() {
    let statsContainer = document.querySelector('.eo-ppd-stats') as HTMLElement;
    if(!statsContainer) {
        return;
    }

    let payload : EoPpdStatsPayload = JSON.parse(statsContainer.getAttribute('data-eo-ppd-stats-json') || '{}');
    let classesById = new Map<string, EoPpdClass>();
    (payload.classes || []).forEach((c) => classesById.set(String(c.id), c));

    let hpEl = document.querySelector('.js-eo-ppd-stat-hp') as HTMLElement;
    let tpEl = document.querySelector('.js-eo-ppd-stat-tp') as HTMLElement;
    let spEl = document.querySelector('.js-eo-ppd-stat-sp') as HTMLElement;
    let weightEl = document.querySelector('.js-eo-ppd-stat-weight') as HTMLElement;
    let damageEl = document.querySelector('.js-eo-ppd-stat-damage') as HTMLElement;
    let hitRateEl = document.querySelector('.js-eo-ppd-stat-hit-rate') as HTMLElement;
    let armorEl = document.querySelector('.js-eo-ppd-stat-armor') as HTMLElement;
    let evadeEl = document.querySelector('.js-eo-ppd-stat-evade') as HTMLElement;
    let criticalEl = document.querySelector('.js-eo-ppd-stat-critical') as HTMLElement;
    let rangeEl = document.querySelector('.js-eo-ppd-stat-range') as HTMLElement;

    function readInt(selector : string) : number {
        let el = document.querySelector(selector) as HTMLInputElement;
        let val = el ? parseInt(el.value, 10) : 0;
        return isNaN(val) ? 0 : val;
    }

    function resolveEquippedStats(selector : string, slot : keyof EoPpdEquipmentStats) : EoPpdItemStats | null {
        let el = document.querySelector(selector) as HTMLSelectElement;
        let id = el ? el.value : '0';
        if(!id || id === '0') {
            return null;
        }
        let slotItems = payload.equipmentStats ? payload.equipmentStats[slot] : null;
        return (slotItems && slotItems[id]) ? slotItems[id] : null;
    }

    function recompute() {
        // ---- BASE layer — HP/TP/SP/Weight from Level + the user's OWN entered
        // STR/INT/WIS/CON (eoserv formulas.ini "Closest to official server",
        // ported verbatim from tools/probe-209.php's eo209_base_stats() golden-
        // value mirror — the regression anchor this port is diffed against
        // during UAT). NO class adjustment is applied here (RESEARCH Pitfall 2 —
        // this server's eo_classes stat columns do not hold bonus base
        // attributes; Class contributes only through the BONUS layer below).
        let level = readInt('.js-eo-ppd-level') || 1;
        let str = readInt('.js-eo-ppd-attr-str');
        let intl = readInt('.js-eo-ppd-attr-int');
        let wis = readInt('.js-eo-ppd-attr-wis');
        let con = readInt('.js-eo-ppd-attr-con');
        // AGI/CHA are accepted D-04 controls but have no verified role in any
        // formula ported here — see the withheld class-family note below.
        readInt('.js-eo-ppd-attr-agi');
        readInt('.js-eo-ppd-attr-cha');

        let f = payload.formula.base;
        let tier = Math.floor(level / 10);
        let common = f.baseConst + level * f.levelMult + (f.tierBlockMult * tier * (tier + 1)) + tier * (level % 10);
        let baseHp = Math.floor(common + con * f.hpConMult * (tier + 1));
        let baseTp = Math.floor(common + intl * f.tpIntMult * (tier + 1) + wis * f.tpWisMult * (tier + 1));
        let spTier = Math.floor(level / 20);
        let baseSp = f.spBaseConst + level + (f.spTierBlockMult * spTier * (spTier + 1)) + spTier * (level % 20) + Math.floor((con + 1) / f.spConDivisor);
        let baseWeight = f.weightBaseConst + str;

        // ---- BONUS layer — equipped gear + the selected class, summed through
        // the SAME getCompareItem() multiplier constants this codebase already
        // ships (EndlessModel.php:1864-1871), matching tools/probe-209.php's
        // eo209_bonus_rollup() exactly. The class bonus is fed through this SAME
        // multiplier layer — NOT added to the base attributes above.
        let classSelect = document.querySelector('.js-eo-ppd-select-class') as HTMLSelectElement;
        let selectedClass = classSelect ? classesById.get(classSelect.value) : undefined;

        let sum = {power: 0, accuracy: 0, defense: 0, dexterity: 0, vitality: 0, aura: 0};
        let critTotal = 0;
        let rangeTotal = 0;

        let equippedSlots : [string, keyof EoPpdEquipmentStats][] = [
            ['.js-eo-ppd-select-hat', 'hats'],
            ['.js-eo-ppd-select-clothing', 'clothing'],
            ['.js-eo-ppd-select-boots', 'boots'],
            ['.js-eo-ppd-select-weapon', 'weapons'],
            ['.js-eo-ppd-select-shield', 'shields'],
        ];
        equippedSlots.forEach(([selector, slot]) => {
            let stats = resolveEquippedStats(selector, slot);
            if(!stats) {
                return;
            }
            sum.power += stats.power || 0;
            sum.accuracy += stats.accuracy || 0;
            sum.defense += stats.defense || 0;
            sum.dexterity += stats.dexterity || 0;
            sum.vitality += stats.vitality || 0;
            sum.aura += stats.aura || 0;
            // ---- FLAT gear-only rollups (RESEARCH Open Question 2 resolution,
            // D-06) — raw eo_items critical_hit/range summed across equipped
            // slots ONLY. NEVER run through a derived formula, and NEVER given a
            // class contribution — eo_classes has no critical_hit/range column.
            critTotal += stats.critical_hit || 0;
            rangeTotal += stats.range || 0;
        });

        if(selectedClass) {
            sum.power += selectedClass.pow || 0;
            sum.accuracy += selectedClass.acc || 0;
            sum.defense += selectedClass.def || 0;
            sum.dexterity += selectedClass.dex || 0;
            sum.vitality += selectedClass.vit || 0;
            sum.aura += selectedClass.aur || 0;
        }
        // Deliberately NOT implemented: eoserv's class-family (Melee/Rogue/Magic/
        // Archer/Peasant) STR/AGI/CON-divisor damage/accuracy/evade/armor
        // sub-formula. This server's eo_classes.type values (0-3, a custom
        // Wanderer/Warrior-Paladin/Rogue-Archer/Magician-Priest pairing) do not
        // match eoserv's canonical 0-4 family index, so it is withheld rather
        // than transplanted against the wrong mapping (RESEARCH.md Anti-Patterns
        // / Pitfall 2, D-06's "withhold rather than show garbage" discipline).

        let m = payload.formula.bonusMultipliers;
        let hpBonus = sum.vitality * m.vitalityToHp;
        let tpBonus = sum.aura * m.auraToTp;
        let damageBonus = sum.power * m.powerToDamage;
        let hitRateBonus = (sum.accuracy * m.accuracyToHitRate) + (sum.dexterity * m.dexterityToHitRate);
        let armorBonus = sum.defense * m.defenseToArmor;
        let evadeBonus = (sum.dexterity * m.dexterityToEvade) + (sum.accuracy * m.accuracyToEvade);

        // ---- Render. TOTAL HP/TP = BASE + BONUS. SP/Weight are base-only (no
        // verified gear/class formula exists for either). Damage/Hit Rate/Armor/
        // Evade are bonus-only — no verified base-attribute component exists in
        // the reference formula, so none is invented (D-06). Hit Rate is
        // EXACTLY the getCompareItem accuracy×4+dexterity rollup (must_haves);
        // Critical Hit/Range are the flat gear-only totals computed above.
        if(hpEl) hpEl.textContent = String(baseHp + hpBonus);
        if(tpEl) tpEl.textContent = String(baseTp + tpBonus);
        if(spEl) spEl.textContent = String(baseSp);
        if(weightEl) weightEl.textContent = String(baseWeight);
        if(damageEl) damageEl.textContent = String(damageBonus);
        if(hitRateEl) hitRateEl.textContent = String(hitRateBonus);
        if(armorEl) armorEl.textContent = String(armorBonus);
        if(evadeEl) evadeEl.textContent = String(evadeBonus);
        if(criticalEl) criticalEl.textContent = String(critTotal);
        if(rangeEl) rangeEl.textContent = String(rangeTotal);
    }

    // Wire recompute to fire on ANY new input/select change AND the existing
    // equipment selects already wired above for the visual image swap (D-07) —
    // this is an ADDITIONAL listener on the same selectors/event, it does not
    // replace update_*_image()'s own wiring.
    let recomputeSelectors = [
        '.js-eo-ppd-attr-str', '.js-eo-ppd-attr-int', '.js-eo-ppd-attr-wis',
        '.js-eo-ppd-attr-agi', '.js-eo-ppd-attr-con', '.js-eo-ppd-attr-cha',
        '.js-eo-ppd-level', '.js-eo-ppd-select-class',
        '.js-eo-ppd-select-hat', '.js-eo-ppd-select-clothing', '.js-eo-ppd-select-boots',
        '.js-eo-ppd-select-weapon', '.js-eo-ppd-select-shield',
    ].join(', ');
    on('change', recomputeSelectors, recompute);

    recompute();
}

function paperdoll() {
    let container = document.querySelector('.eo-ppd') as HTMLElement;
    if(!container) {
        return;
    }

    on('change', '.js-eo-ppd-select-race', () => {
        update_race_image();
    });

    on('change', '.js-eo-ppd-select-gender', () => {
        update_race_image();
        update_hair_image();
        update_hat_image();
        update_clothing_image();
        update_boots_image();
        update_weapon_image();
        update_shield_image();
    });

    on('change', '.js-eo-ppd-select-direction', () => {
        update_race_image();
        update_hair_image();
        update_hat_image();
        update_clothing_image();
        update_boots_image();
        update_weapon_image();
        update_shield_image();
    });

    on('change', '.js-eo-ppd-select-hair', () => {
        update_hair_image();
    });

    on('change', '.js-eo-ppd-select-color', () => {
        update_hair_image();
    });

    on('change', '.js-eo-ppd-select-hat', () => {
        update_hat_image();
    });

    on('change', '.js-eo-ppd-select-clothing', () => {
        update_clothing_image();
    });

    on('change', '.js-eo-ppd-select-boots', () => {
        update_boots_image();
    });

    on('change', '.js-eo-ppd-select-weapon', () => {
        update_weapon_image();
    });

    on('change', '.js-eo-ppd-select-shield', () => {
        update_shield_image();
    });

    let ppdContainer = document.querySelector('.eo-ppd') as HTMLElement;
    ppdContainer.addEventListener('click', () => {
        let directionSelect = document.querySelector('.js-eo-ppd-select-direction') as HTMLSelectElement;
        let direction = parseInt(directionSelect.value);
        direction = (direction + 1) % 4;
        directionSelect.value = direction.toString();
        directionSelect.dispatchEvent(new Event('change'));
    });

    let race = document.querySelector('.eo-ppd-race') as HTMLImageElement;
    let hair = document.querySelector('.eo-ppd-hair') as HTMLImageElement;
    let hat = document.querySelector('.eo-ppd-hat') as HTMLImageElement;
    let hatUnder = document.querySelector('.eo-ppd-hat-under') as HTMLImageElement;
    let clothing = document.querySelector('.eo-ppd-clothing') as HTMLImageElement;
    let boots = document.querySelector('.eo-ppd-boots') as HTMLImageElement;
    let weapon = document.querySelector('.eo-ppd-weapon') as HTMLImageElement;
    let shield = document.querySelector('.eo-ppd-shield') as HTMLImageElement;
    let quiver = document.querySelector('.eo-ppd-quiver') as HTMLImageElement;
    let quiverUnder = document.querySelector('.eo-ppd-quiver-under') as HTMLImageElement;

    race.onload = () => race.style.display = '';
    hair.onload = () => hair.style.display = '';
    hat.onload = () => hat.style.display = '';
    hatUnder.onload = () => hatUnder.style.display = '';
    clothing.onload = () => clothing.style.display = '';
    boots.onload = () => boots.style.display = '';
    weapon.onload = () => weapon.style.display = '';
    shield.onload = () => shield.style.display = '';
    quiver.onload = () => quiver.style.display = '';
    quiverUnder.onload = () => quiverUnder.style.display = '';

    function update_image(element: HTMLImageElement, type: string, ...args: any[]) {
        let gender = (document.querySelector('.js-eo-ppd-select-gender') as HTMLSelectElement).value;
        let direction = (document.querySelector('.js-eo-ppd-select-direction') as HTMLSelectElement).value;
        element.setAttribute('src', `/assets/eo/${eo_version}/${type}/${args.join('-')}-${gender}-${direction}.png`);
        element.setAttribute('data-gender', gender);
        element.setAttribute('data-direction', direction);
        setTimeout(() => {
            if(!element.complete) {
                element.style.display = 'none';
            }
        },1);
    }

    function update_race_image() {
        let raceVal = (document.querySelector('.js-eo-ppd-select-race') as HTMLSelectElement).value;
        update_image(race,'race',raceVal);
    }

    function update_hair_image() {
        let hairVal = (document.querySelector('.js-eo-ppd-select-hair') as HTMLSelectElement).value;
        let colorVal = (document.querySelector('.js-eo-ppd-select-color') as HTMLSelectElement).value;
        update_image(hair,'hair',hairVal,colorVal);
    }

    function update_hat_image() {
        let hatVal = (document.querySelector('.js-eo-ppd-select-hat') as HTMLSelectElement).value;
        update_image(hat,'hat',hatVal,0);
        update_image(hatUnder,'hat',hatVal,1);
    }

    function update_clothing_image() {
        let clothingVal = (document.querySelector('.js-eo-ppd-select-clothing') as HTMLSelectElement).value;
        update_image(clothing,'clothing',clothingVal);
    }

    function update_boots_image() {
        let bootsVal = (document.querySelector('.js-eo-ppd-select-boots') as HTMLSelectElement).value;
        update_image(boots,'boots',bootsVal);
    }

    function update_weapon_image() {
        let weaponVal = (document.querySelector('.js-eo-ppd-select-weapon') as HTMLSelectElement).value;
        update_image(weapon,'weapon',weaponVal);
    }

    function update_shield_image() {
        let shieldVal = (document.querySelector('.js-eo-ppd-select-shield') as HTMLSelectElement).value;
        update_image(shield,'shield',shieldVal);
        update_image(quiver,'quiver',shieldVal,0);
        update_image(quiverUnder,'quiver',shieldVal,1);
    }

    computeCharacterStats();
}
